/**
 * Perfil do cliente na ficha (sem React, testável): quem é quem nas
 * conversas, as escalas que o Jev mede e o texto que o Gemini escreve.
 * Tudo vem do servidor; aqui só se lê com tolerância a campo ausente.
 */
import { plural } from "../../../utils/dinheiro";

/** Papéis na ordem em que a lista mostra (o cliente primeiro). */
export const PAPEIS = Object.freeze([
  { valor: "cliente", rotulo: "Cliente", ajuda: "Quem fechou o pedido" },
  { valor: "familiar", rotulo: "Familiar", ajuda: "Cônjuge ou família do cliente" },
  { valor: "arquiteto", rotulo: "Arquiteto", ajuda: "Arquiteto ou designer do cliente" },
  { valor: "prestador", rotulo: "Prestador", ajuda: "Montador, fornecedor, obra" },
  { valor: "equipe", rotulo: "Equipe da empresa", ajuda: "Gente da própria marcenaria" },
  { valor: "outro", rotulo: "Outro", ajuda: "Outra pessoa" },
  { valor: "nao_identificado", rotulo: "Não identificado", ajuda: "A IA não soube dizer" },
]);

const ORDEM_DO_PAPEL = new Map(PAPEIS.map((p, i) => [p.valor, i]));
const ORIGEM_MANUAL = new Set(["manual", "voce", "você", "pessoa", "usuario"]);

export function rotuloDoPapel(participante) {
  if (participante?.papel_rotulo) return participante.papel_rotulo;
  return PAPEIS.find((p) => p.valor === participante?.papel)?.rotulo || "Não identificado";
}

function porcentagem(valor) {
  const n = Number(valor);
  return Number.isFinite(n) && n > 0 ? `${Math.round(Math.min(1, n) * 100)}%` : "";
}

/** "pela regra", "pela IA (82%)" ou "corrigido à mão". */
export function origemDoParticipante(participante) {
  const origem = String(participante?.origem || "").toLowerCase();
  if (origem === "regra") return "pela regra";
  if (origem === "ia") {
    const confianca = porcentagem(participante.confianca);
    return confianca ? `pela IA (${confianca})` : "pela IA";
  }
  if (ORIGEM_MANUAL.has(origem)) return "corrigido à mão";
  return "";
}

/** Cliente primeiro, depois a família, o arquiteto...; no mesmo papel, pelo nome. */
export function ordenarParticipantes(participantes) {
  const ordem = (p) => ORDEM_DO_PAPEL.get(p?.papel) ?? PAPEIS.length;
  return [...(participantes || [])].sort(
    (a, b) => ordem(a) - ordem(b) || String(a?.nome || "").localeCompare(String(b?.nome || ""), "pt-BR"),
  );
}

/** As três escalas, cada uma de 0 a 1 em cinco faixas com nome. */
export const ESCALAS = Object.freeze([
  {
    chave: "satisfacao",
    rotulo: "Satisfação",
    faixas: ["Muito insatisfeito", "Insatisfeito", "Neutro", "Satisfeito", "Muito satisfeito"],
  },
  {
    chave: "temperamento",
    rotulo: "Temperamento",
    faixas: ["Muito irritado", "Irritado", "Moderado", "Tranquilo", "Muito tranquilo"],
  },
  {
    chave: "exigencia",
    rotulo: "Exigência",
    faixas: ["Nada exigente", "Pouco exigente", "Exigência média", "Exigente", "Muito exigente"],
  },
]);

const CONFIANCA_ALTA = 0.75;
const CONFIANCA_MEDIA = 0.5;

const TENDENCIAS = Object.freeze({
  melhorando: { texto: "melhorando", tom: "neutral" },
  estavel: { texto: "estável", tom: "neutral" },
  piorando: { texto: "piorando", tom: "warning" },
});

function nota(dado) {
  const n = Number(dado?.nota ?? dado?.valor);
  return Number.isFinite(n) ? Math.min(1, Math.max(0, n)) : null;
}

function textoDaConfianca(valor) {
  const n = Number(valor);
  if (!Number.isFinite(n)) return "";
  if (n >= CONFIANCA_ALTA) return "confiança alta";
  if (n >= CONFIANCA_MEDIA) return "confiança média";
  return "confiança baixa";
}

function tendencia(valor) {
  const chave = String(valor || "")
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "")
    .toLowerCase();
  return TENDENCIAS[chave] || null;
}

/**
 * Leitura de uma escala: a faixa em palavras, a nota, a confiança e a
 * tendência (cor só quando piora). → null quando a escala não veio.
 */
export function leituraDaEscala(escala, dado) {
  const valor = nota(dado);
  if (valor === null) return null;
  const faixa = Math.min(escala.faixas.length - 1, Math.floor(valor * escala.faixas.length));
  return {
    chave: escala.chave,
    rotulo: escala.rotulo,
    palavra: escala.faixas[faixa],
    nota: valor,
    confianca: textoDaConfianca(dado.confianca),
    tendencia: tendencia(dado.tendencia),
  };
}

/** As escalas que vieram, na ordem da tela. */
export function leiturasDasEscalas(escalas) {
  if (!escalas) return [];
  return ESCALAS.map((escala) => leituraDaEscala(escala, escalas[escala.chave])).filter(Boolean);
}

/** "Pouca conversa para avaliar" com os números, quando o servidor os manda. */
export function textoDePoucaConversa(escalas) {
  const mensagens = Number(escalas?.mensagens);
  const minimo = Number(escalas?.minimo);
  if (Number.isFinite(mensagens) && Number.isFinite(minimo) && minimo > 0) {
    return `Pouca conversa para avaliar: ${plural(mensagens, "mensagem", "mensagens")} do cliente, a avaliação começa com ${minimo}.`;
  }
  return "Pouca conversa para avaliar: a avaliação começa quando o cliente tiver mais mensagens.";
}

/** Partes do texto do perfil, na ordem em que a tela mostra. */
export const SECOES_DO_PERFIL = Object.freeze([
  { chave: "resumo", rotulo: "Resumo" },
  { chave: "comunicacao", rotulo: "Como se comunica" },
  { chave: "quem_decide", rotulo: "Quem decide" },
  { chave: "valoriza", rotulo: "O que valoriza" },
  { chave: "reclamacoes", rotulo: "Reclamações" },
  { chave: "cuidados", rotulo: "Cuidados ao falar" },
]);

function idsDe(item) {
  const ids = item?.mensagens ?? item?.refs ?? item?.mensagens_origem ?? [];
  return Array.isArray(ids) ? ids.filter((id) => id !== null && id !== undefined && id !== "") : [];
}

function itemDoTexto(item) {
  if (typeof item === "string") return { texto: item.trim(), mensagens: [] };
  const texto = String(item?.texto ?? item?.item ?? item?.descricao ?? "").trim();
  return { texto, mensagens: idsDe(item) };
}

/** Uma parte do texto (texto solto, item com refs ou lista de itens) → [{texto, mensagens}]. */
export function itensDaSecao(valor) {
  if (valor === null || valor === undefined) return [];
  const lista = Array.isArray(valor) ? valor : [valor];
  return lista.map(itemDoTexto).filter((item) => item.texto);
}

/** As partes do texto que têm conteúdo, na ordem da tela. */
export function secoesDoTexto(texto) {
  if (!texto) return [];
  return SECOES_DO_PERFIL.map((secao) => ({ ...secao, itens: itensDaSecao(texto[secao.chave]) })).filter(
    (secao) => secao.itens.length,
  );
}
